
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Search, MoreHorizontal, Users, UserCheck, RefreshCw } from "lucide-react";
import { useQuery } from '@tanstack/react-query';
import { affiliateService } from '@/services/affiliateService';
import { toast } from 'sonner';
import { MobileAffiliateCard } from '@/components/admin/affiliate/MobileAffiliateCard';
import { UnassignedUsersTable } from '@/components/admin/affiliate/UnassignedUsersTable';
import { AssignedUsersTable } from '@/components/admin/affiliate/AssignedUsersTable';
import { useIsMobile } from '@/hooks/use-mobile';

export const ManageAffiliatesPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const isMobile = useIsMobile();
  
  const { data: affiliates, isLoading, refetch } = useQuery({
    queryKey: ['adminAffiliates', statusFilter],
    queryFn: () => affiliateService.getAdminAffiliates(statusFilter),
  });

  const handleStatusChange = async (affiliateId: string, newStatus: 'active' | 'suspended' | 'pending') => {
    try {
      const success = await affiliateService.updateAffiliateStatus(affiliateId, newStatus);
      if (success) {
        toast.success(`Affiliate status updated to ${newStatus}`);
        refetch();
      } else {
        toast.error("Failed to update affiliate status");
      }
    } catch (error) {
      console.error("Error updating affiliate status:", error);
      toast.error("An error occurred while updating affiliate status");
    }
  };

  const handleCommissionChange = async (affiliateId: string, newRate: number) => {
    try {
      const success = await affiliateService.updateAffiliateCommissionRate(affiliateId, newRate);
      if (success) {
        toast.success(`Commission rate updated to ${newRate * 100}%`);
        refetch();
      } else {
        toast.error("Failed to update commission rate");
      }
    } catch (error) {
      console.error("Error updating commission rate:", error);
      toast.error("An error occurred while updating commission rate");
    }
  };

  const filteredAffiliates = affiliates ? affiliates.filter(affiliate => 
    affiliate.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    affiliate.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    affiliate.referral_code.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  const getBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-amber-100 text-amber-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return <div className="p-8">Loading affiliates...</div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Manage Affiliates</h1>
        <p className="text-muted-foreground mt-1">Review and manage your affiliate partners</p>
      </div>
      
      <Tabs defaultValue="affiliates" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="affiliates" className="flex items-center gap-2">
            <UserCheck className="h-4 w-4" />
            Affiliates
          </TabsTrigger>
          <TabsTrigger value="unassigned" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Unassigned
          </TabsTrigger>
          <TabsTrigger value="assigned" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Assigned
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="affiliates" className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Affiliates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search affiliates..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filteredAffiliates.length > 0 ? (
                isMobile ? (
                  // Mobile card layout
                  <div className="space-y-3">
                    {filteredAffiliates.map((affiliate) => (
                      <MobileAffiliateCard
                        key={affiliate.id}
                        affiliate={affiliate}
                        onStatusChange={handleStatusChange}
                        onCommissionChange={handleCommissionChange}
                      />
                    ))}
                  </div>
                ) : (
                  // Desktop table layout
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Referral Code</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Commission</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAffiliates.map((affiliate) => (
                          <TableRow key={affiliate.id}>
                            <TableCell>{affiliate.name}</TableCell>
                            <TableCell>{affiliate.email}</TableCell>
                            <TableCell>
                              <code className="text-xs bg-muted px-1 py-0.5 rounded">
                                {affiliate.referral_code}
                              </code>
                            </TableCell>
                            <TableCell>
                              <Badge className={getBadgeColor(affiliate.status)}>
                                {affiliate.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{(affiliate.commission_rate * 100).toFixed(0)}%</TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                    <span className="sr-only">Open menu</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleStatusChange(affiliate.id, 'active')}>
                                    Set as Active
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleStatusChange(affiliate.id, 'pending')}>
                                    Set as Pending
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleStatusChange(affiliate.id, 'suspended')}>
                                    Suspend Affiliate
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleCommissionChange(affiliate.id, 0.05)}>
                                    Set Commission: 5%
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleCommissionChange(affiliate.id, 0.10)}>
                                    Set Commission: 10%
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleCommissionChange(affiliate.id, 0.15)}>
                                    Set Commission: 15%
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  No affiliates found matching your criteria
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="unassigned" className="space-y-6">
          <UnassignedUsersTable />
        </TabsContent>

        <TabsContent value="assigned" className="space-y-6">
          <AssignedUsersTable />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ManageAffiliatesPage;
